import {
  ALINE_DRAFT_DATA_ATTRIBUTES,
  getAlineDraftListMarkerStyleName,
  getListMarkerText,
  isListLevel,
  LIST_ATTRIBUTE_NAMES,
} from '@aline/common';
import type { CastParagraph, ListLevel } from '@aline/common';
import DOMPurify from 'dompurify';

/**
 * Synthetic list id given to editor/insert list items (`<p list-level
 * list-type>`), which carry no id of their own. Like the apply path's single
 * `SYMMETRIC_LIST_SOURCE_ID`, one shared id groups them into a single list so
 * preview numbering matches the applied document.
 */
const EDITOR_INSERT_LIST_GROUP_KEY = 'editor-insert-list';

const BULLET_LIST_TYPE = 'bullet' satisfies NonNullable<CastParagraph['listType']>;

const DRAFT_PREVIEW_ALLOWED_TAGS = [
  'a',
  'b',
  'br',
  'code',
  'del',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'i',
  'li',
  'ol',
  'p',
  'pre',
  's',
  'span',
  'strong',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
];

const DRAFT_PREVIEW_ALLOWED_ATTRIBUTES = [
  'colspan',
  'href',
  'rowspan',
  'style',
  'title',
  ...Object.values(ALINE_DRAFT_DATA_ATTRIBUTES),
];
const DRAFT_PREVIEW_ALLOWED_ATTRIBUTE_SET = new Set(DRAFT_PREVIEW_ALLOWED_ATTRIBUTES);
const DRAFT_PREVIEW_ALLOWED_DATA_ATTRIBUTES = Object.values(ALINE_DRAFT_DATA_ATTRIBUTES);
const DRAFT_PREVIEW_ALLOWED_DATA_ATTRIBUTE_SET = new Set<string>(
  DRAFT_PREVIEW_ALLOWED_DATA_ATTRIBUTES
);

const DRAFT_PREVIEW_ALLOWED_STYLE_PROPERTIES = new Set([
  'background-color',
  'color',
  'font-weight',
  'margin-bottom',
  'margin-top',
  'text-align',
]);
export const DRAFT_PREVIEW_DATA_ATTRIBUTES = {
  listContent: 'data-aline-preview-list-content',
  listItem: 'data-aline-preview-list-item',
  listLevel: 'data-aline-preview-list-level',
  listMarker: 'data-aline-preview-list-marker',
} as const;

export type DraftPreviewDataAttribute =
  (typeof DRAFT_PREVIEW_DATA_ATTRIBUTES)[keyof typeof DRAFT_PREVIEW_DATA_ATTRIBUTES];

DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
  if (DRAFT_PREVIEW_ALLOWED_ATTRIBUTE_SET.has(data.attrName)) data.keepAttr = true;
});

export function sanitizeHtmlForEditorPaste(html: string) {
  return DOMPurify.sanitize(html, {
    FORBID_ATTR: ['style', 'class'],
    FORBID_TAGS: ['style'],
  });
}

export function sanitizeHtmlForDraftPreview(html: string) {
  const sanitizedHtml = DOMPurify.sanitize(html, {
    ADD_ATTR: DRAFT_PREVIEW_ALLOWED_ATTRIBUTES,
    ALLOW_DATA_ATTR: true,
    ALLOWED_TAGS: DRAFT_PREVIEW_ALLOWED_TAGS,
    ALLOWED_URI_REGEXP: /^(https?:|mailto:|#|\/)/i,
  });
  return sanitizeDraftPreviewAttributes(sanitizedHtml, html);
}

export function prepareHtmlForPreview(html: string) {
  /**
   * Normalize the editor/insert list shape to the drafter's `data-aline-*`
   * shape BEFORE sanitizing — DOMPurify drops non-`data-` custom attributes, so
   * `list-level` / `list-type` wouldn't survive on their own. Both encodings
   * then flow through the one marker renderer below.
   */
  return renderAlineDraftListMarkersForPreview(
    sanitizeHtmlForDraftPreview(normalizeInsertListAttributes(html))
  );
}

export function renderAlineDraftListMarkersForPreview(sanitizedHtml: string) {
  const document = new DOMParser().parseFromString(sanitizedHtml, 'text/html');
  removeExistingPreviewListMarkers(document);

  const listPaths = new Map<string, number[]>();
  for (const element of Array.from(
    document.body.querySelectorAll(`[${ALINE_DRAFT_DATA_ATTRIBUTES.listId}]`)
  )) {
    const listId = element.getAttribute(ALINE_DRAFT_DATA_ATTRIBUTES.listId)?.trim();
    if (!listId) continue;

    const listLevel = getPreviewListLevel(
      element.getAttribute(ALINE_DRAFT_DATA_ATTRIBUTES.listLevel)
    );
    if (typeof listLevel !== 'number') continue;

    const listPath = getNextPreviewListPath(listPaths.get(listId), listLevel);
    listPaths.set(listId, listPath);

    const markerText = getListMarkerText(
      {
        style: {
          name: getAlineDraftListMarkerStyleName(
            element.getAttribute(ALINE_DRAFT_DATA_ATTRIBUTES.listStyle)
          ),
        },
      },
      { listLevel, listPath }
    );

    wrapPreviewListItemContent(document, element);
    element.setAttribute(DRAFT_PREVIEW_DATA_ATTRIBUTES.listItem, 'true');
    element.setAttribute(DRAFT_PREVIEW_DATA_ATTRIBUTES.listLevel, String(listLevel));

    const markerElement = document.createElement('span');
    markerElement.setAttribute(DRAFT_PREVIEW_DATA_ATTRIBUTES.listMarker, 'true');
    markerElement.setAttribute('aria-hidden', 'true');
    markerElement.textContent = markerText;
    element.insertBefore(markerElement, element.firstChild);
  }

  return document.body.innerHTML;
}

/**
 * Converts editor/insert list items (`<p list-level list-type>` — 1-based
 * level, no id) into the drafter's `data-aline-list-*` shape so the single
 * marker renderer handles both encodings. A shared synthetic id groups them
 * into one list (matching the applied document), and `list-type` maps to the
 * same marker the apply path uses (`bullet` → black circle, otherwise decimal).
 * Runs before sanitization because DOMPurify drops non-`data-` custom
 * attributes like `list-level`.
 */
function normalizeInsertListAttributes(html: string): string {
  // Runs on every streamed token; skip the DOM round-trip when there is nothing to convert.
  if (!html.includes(LIST_ATTRIBUTE_NAMES.listLevel)) return html;

  const document = new DOMParser().parseFromString(html, 'text/html');

  for (const element of Array.from(
    document.body.querySelectorAll(`[${LIST_ATTRIBUTE_NAMES.listLevel}]`)
  )) {
    const oneBasedLevel = Number.parseInt(
      element.getAttribute(LIST_ATTRIBUTE_NAMES.listLevel) ?? '',
      10
    );
    const listType = element.getAttribute(LIST_ATTRIBUTE_NAMES.listType)?.trim().toLowerCase();
    element.removeAttribute(LIST_ATTRIBUTE_NAMES.listLevel);
    element.removeAttribute(LIST_ATTRIBUTE_NAMES.listType);
    if (!Number.isFinite(oneBasedLevel) || oneBasedLevel < 1) continue;

    element.setAttribute(ALINE_DRAFT_DATA_ATTRIBUTES.listId, EDITOR_INSERT_LIST_GROUP_KEY);
    element.setAttribute(ALINE_DRAFT_DATA_ATTRIBUTES.listLevel, String(oneBasedLevel - 1));
    element.setAttribute(
      ALINE_DRAFT_DATA_ATTRIBUTES.listStyle,
      listType === BULLET_LIST_TYPE ? 'black-circle' : 'decimal'
    );
  }

  return document.body.innerHTML;
}

function sanitizeDraftPreviewAttributes(html: string, sourceHtml: string) {
  const document = new DOMParser().parseFromString(html, 'text/html');
  copySafeTableSpanAttributes(sourceHtml, document);

  for (const element of Array.from(document.body.querySelectorAll('*'))) {
    for (const attribute of Array.from(element.attributes)) {
      if (!DRAFT_PREVIEW_ALLOWED_ATTRIBUTE_SET.has(attribute.name)) {
        element.removeAttribute(attribute.name);
        continue;
      }

      if (
        attribute.name.startsWith('data-') &&
        !DRAFT_PREVIEW_ALLOWED_DATA_ATTRIBUTE_SET.has(attribute.name)
      ) {
        element.removeAttribute(attribute.name);
      }
    }
  }

  for (const element of Array.from(document.body.querySelectorAll('[style]'))) {
    const sanitizedStyle = sanitizeStyle(element.getAttribute('style') ?? '');
    if (sanitizedStyle) {
      element.setAttribute('style', sanitizedStyle);
      continue;
    }

    element.removeAttribute('style');
  }

  return document.body.innerHTML;
}

function removeExistingPreviewListMarkers(document: Document) {
  for (const element of Array.from(
    document.body.querySelectorAll(`[${DRAFT_PREVIEW_DATA_ATTRIBUTES.listMarker}]`)
  )) {
    element.remove();
  }

  for (const element of Array.from(
    document.body.querySelectorAll(`[${DRAFT_PREVIEW_DATA_ATTRIBUTES.listContent}]`)
  )) {
    element.replaceWith(...Array.from(element.childNodes));
  }

  for (const element of Array.from(
    document.body.querySelectorAll(`[${DRAFT_PREVIEW_DATA_ATTRIBUTES.listItem}]`)
  )) {
    element.removeAttribute(DRAFT_PREVIEW_DATA_ATTRIBUTES.listItem);
    element.removeAttribute(DRAFT_PREVIEW_DATA_ATTRIBUTES.listLevel);
  }
}

function getPreviewListLevel(value: string | null): ListLevel | null {
  const parsedValue = Number.parseInt(value ?? '', 10);
  if (!Number.isFinite(parsedValue) || parsedValue < 0) return null;

  const normalizedValue = Math.min(parsedValue, 8);
  if (!isListLevel(normalizedValue)) return null;

  return normalizedValue;
}

function getNextPreviewListPath(previousPath: number[] | undefined, listLevel: ListLevel) {
  return Array.from({ length: listLevel + 1 }, (_, index) => {
    const previousValue = previousPath?.[index];
    if (typeof previousValue !== 'number') return 0;
    return index === listLevel ? previousValue + 1 : previousValue;
  });
}

function wrapPreviewListItemContent(document: Document, element: Element) {
  const contentElement = document.createElement('span');
  contentElement.setAttribute(DRAFT_PREVIEW_DATA_ATTRIBUTES.listContent, 'true');

  for (const childNode of Array.from(element.childNodes)) {
    contentElement.appendChild(childNode);
  }
  element.appendChild(contentElement);
}

function copySafeTableSpanAttributes(sourceHtml: string, sanitizedDocument: Document) {
  const sourceDocument = new DOMParser().parseFromString(sourceHtml, 'text/html');
  const sourceCells = Array.from(sourceDocument.body.querySelectorAll('td, th'));
  const sanitizedCells = Array.from(sanitizedDocument.body.querySelectorAll('td, th'));

  sourceCells.forEach((sourceCell, index) => {
    const sanitizedCell = sanitizedCells[index];
    if (!sanitizedCell) return;

    copyPositiveIntegerAttribute(sourceCell, sanitizedCell, 'colspan');
    copyPositiveIntegerAttribute(sourceCell, sanitizedCell, 'rowspan');
  });
}

function copyPositiveIntegerAttribute(
  sourceElement: Element,
  targetElement: Element,
  attributeName: string
) {
  const value = sourceElement.getAttribute(attributeName);
  if (!value?.match(/^[1-9]\d*$/)) return;

  targetElement.setAttribute(attributeName, value);
}

function sanitizeStyle(value: string) {
  return value
    .split(';')
    .map(part => part.trim())
    .filter(part => {
      const [property, ...valueParts] = part.split(':');
      if (!property || !valueParts.length) return false;

      const propertyName = property.trim().toLowerCase();
      const propertyValue = valueParts.join(':').trim();
      return (
        DRAFT_PREVIEW_ALLOWED_STYLE_PROPERTIES.has(propertyName) &&
        !/url\s*\(|expression\s*\(/i.test(propertyValue)
      );
    })
    .join('; ');
}
